import React, { useRef, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import classnames from 'classnames';
import { MessagePropertiesContext } from '../../contexts/messageProperties';
import RemoveIcon from '../../../../svg/icons/remove.svg';
import Modal, { ANIMATION_DURATION } from './modal';
import ModalOpenButton from './modalOpenButton';
import styles from './modal.module.pcss';

interface RightSlideInModalProps {
  children: React.ReactElement | React.ReactElement[];
  firstFooterButton?: React.ReactElement | React.ReactElement[];
  secondFooterButton?: React.ReactElement | React.ReactElement[];
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
  heading: string;
  headingId?: string;
  skipRemovingModalRoot?: boolean;
}

function RightSlideInModal({
  children,
  isOpen,
  heading,
  headingId,
  onClose,
  onOpen,
  firstFooterButton,
  secondFooterButton,
  skipRemovingModalRoot
}: RightSlideInModalProps): React.ReactElement {
  const transitions = useTransition(isOpen, null, {
    from: { transform: 'translateX(100%)' },
    enter: { transform: 'translateX(0)' },
    leave: { transform: 'translateX(100%)' },
    config: { duration: ANIMATION_DURATION }
  });

  return (
    <>
      {onOpen && <ModalOpenButton handleOpen={onOpen} ariaLabel={`Open ${heading} modal`} />}
      <Modal isOpen={isOpen} onOverlayClick={onClose} skipRemovingModalRoot={skipRemovingModalRoot}>
        <>
          {transitions.map(
            ({ item: shouldShow, key, props }) =>
              shouldShow && (
                <animated.div
                  key={key}
                  style={props}
                  role="dialog"
                  className={styles['modal--slide-in-modal']}
                  aria-modal="true"
                  aria-labelledby={headingId}
                >
                  <div className={styles['modal__scrollable']}>
                    <ModalHeader heading={heading} headingId={headingId} onClick={onClose} />
                    <div className={styles['modal-content']}>{children}</div>
                  </div>
                  <ModalFooter firstButton={firstFooterButton} secondButton={secondFooterButton} onClick={onClose} />
                </animated.div>
              )
          )}
        </>
      </Modal>
    </>
  );
}

function ModalHeader({
  heading,
  headingId,
  onClick
}: {
  heading?: string;
  headingId?: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <div className={styles['modal-header']}>
      <button aria-label="Close" className={styles['modal-header__close-button']} type="button" onClick={onClick}>
        <RemoveIcon />
      </button>
      <h3 className={styles['modal-header__heading']} id={headingId}>
        {heading}
      </h3>
    </div>
  );
}

function ModalFooter({
  onClick,
  firstButton,
  secondButton
}: {
  onClick: () => void;
  secondButton?: React.ReactElement | React.ReactElement[];
  firstButton?: React.ReactElement | React.ReactElement[];
}): React.ReactElement {
  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, []);
  return (
    <div className={styles['modal-footer']}>
      <div className={classnames(styles['modal-footer__close-button-wrapper'])}>
        {firstButton || (
          <button ref={ref} type="button" onClick={onClick} className="button--secondary-dark">
            {formattedMessage({ id: 'product.finance.modal.close', fallback: 'Close' })}
          </button>
        )}
      </div>
      {secondButton && (
        <div
          className={classnames(styles['modal-footer__second-button-wrapper'], {
            [styles['modal-footer__margin']]: secondButton
          })}
        >
          {secondButton}
        </div>
      )}
    </div>
  );
}

export default RightSlideInModal;
